import { memo } from 'react'
import { AnimatePresence, motion, type Transition } from 'framer-motion'
import type { AssistantMessage, ToolMessage } from '../../../../types'
import { ToolSubmessageRenderer } from '../tools/tool-submessage'
import { AssistantMessageChrome } from './assistant-chrome'

const expandInitial = { height: 0, opacity: 0, scale: 1 }
const expandAnimate = { height: 'auto', opacity: 1, scale: 1 }
const expandExit = { height: 0, opacity: 0, scale: 0.96 }
const expandTransition: Transition = { duration: 0.5, type: 'spring', bounce: 0 }

/**
 * An assistant submessage inside the expansion. Submessages hide their actions
 * row (the spacer keeps the hover geometry), so edit mode is unreachable and
 * no `editing` plumbing is passed.
 */
const AssistantSubmessage = memo(function AssistantSubmessage({ message }: { message: AssistantMessage }) {
  return (
    <AssistantMessageChrome message={message} isSubmessage isTurnActive={false} isMostRecent={false}>
      <div className="relative mt-1 flex items-center" />
    </AssistantMessageChrome>
  )
})

export interface SubmessagesProps {
  isExpanded: boolean
  submessages: Array<AssistantMessage | ToolMessage> | undefined
  onClickSearchResult: (url: string) => void
}

/** The expanded submessage section under the group header. */
export const Submessages = memo(function Submessages({
  isExpanded,
  submessages,
  onClickSearchResult,
}: SubmessagesProps) {
  return (
    <AnimatePresence>
      {isExpanded && submessages && (
        <motion.div
          initial={expandInitial}
          animate={expandAnimate}
          exit={expandExit}
          transition={expandTransition}
          className="overflow-hidden"
        >
          <div className="mx-4 border-l px-4 py-2">
            {submessages.map((message) => {
              if (message.role === 'tool') {
                // Tool submessages carry string results; anything else has no
                // renderable body here.
                if (typeof message.content !== 'string') return null
                return (
                  <ToolSubmessageRenderer
                    key={message.id}
                    // The guard above proves this; TS can't narrow the parent
                    // object from a property typeof check.
                    message={message as ToolMessage & { content: string }}
                    onClickSearchResult={onClickSearchResult}
                  />
                )
              }

              return (
                <div key={message.id} className="mb-[14px]">
                  <AssistantSubmessage message={message} />
                </div>
              )
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})
